import React from 'react';

let translations = {};

export const initI18n = data => {
    translations = data;
};

// --- Dot-path lookup ---
const getValue = (translations, path) => {
    return path.split('.').reduce((acc, key) => acc?.[key], translations);
};

// --- Convert [icon] and [link]tags ---
const isIconToken = token => token.startsWith('icon-');

const isLinkToken = token => token.startsWith('link');

const createIconElement = (token, key) => {
    return <i key={key} className={token.replace('icon-', '').trim()} />;
};

const appendPlainText = (str, parts, start, end) => {
    if (end > start) {
        parts.push(str.slice(start, end));
    }
};

const appendRemainingText = (str, parts, lastIndex) => {
    if (lastIndex < str.length) {
        parts.push(str.slice(lastIndex));
    }
};

const createLinkElement = (token, key) => {
    const regexp = new RegExp(/^(link(?:-new-win)?)\s+([^ ]+)\s+'([^']+)'/); // NOSONAR
    const match = regexp.exec(token);

    if (!match) return null;

    const [, type, href, text] = match;

    return React.createElement(
        'a',
        {
            key,
            href,
            className: 'ec-i18n-link',
            target: type === 'link-new-win' ? '_blank' : undefined,
            rel: type === 'link-new-win' ? 'noopener' : undefined,
        },
        text
    );
};

const createElementFromToken = (token, key) => {
    if (isIconToken(token)) {
        return createIconElement(token, key);
    }

    if (isLinkToken(token)) {
        return createLinkElement(token, key);
    }

    return null;
};

const decodeHtmlEntities = str => {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
};

const convertIconAndLinkTags = str => {
    const parts = [];
    let lastIndex = 0;

    const regexp = /\[(icon-[^\]]+|link(?:-new-win)?\s+[^ ]+\s+'[^']+')\]/g; // NOSONAR

    let match;

    while ((match = regexp.exec(str)) !== null) {
        appendPlainText(str, parts, lastIndex, match.index);

        const token = match[1];
        const element = createElementFromToken(token, parts.length);

        if (element) {
            parts.push(element);
        }

        lastIndex = regexp.lastIndex;
    }

    appendRemainingText(str, parts, lastIndex);

    return parts;
};

// --- Resolve references to other keys ---
const resolveReferences = (str, seen = new Set()) => {
    const regex = /\$t\(([^)]+)\)/g; // NOSONAR

    return str.replace(regex, (_, refKey) => {
        if (seen.has(refKey)) {
            console.warn(`Circular i18n reference detected: ${refKey}`);
            return '';
        }

        seen.add(refKey);

        const refValue = getValue(translations, refKey);

        if (typeof refValue !== 'string') {
            return '';
        }

        return resolveReferences(refValue, seen);
    });
};

// --- String interpolation, replace {{placeholders}} with values ---
const formatString = (str, params = {}) => {
    let resolvedString = resolveReferences(str);

    let result = resolvedString.replace(/\{\{(.*?)\}\}/g, (_, key) => {
        // NOSONAR
        return params[key.trim()] ?? '';
    });

    if (result.includes('&')) {
        result = decodeHtmlEntities(result);
    }

    if (result.includes('[link') || result.includes('[icon')) {
        return convertIconAndLinkTags(result);
    }
    return result;
};

// --- JSX wrapper helper ---
const wrapArray = (arr, tag) => {
    if (!tag) return arr;
    return arr.map((item, index) => React.createElement(tag, { key: item }, item));
    return arr.map((item, index) => React.createElement(tag, { key: index }, item));
};

// --- 4. Main i18n function ---
// i18n("the.key")
// i18n("the.key", {a: <value>, b: <value>})
// i18n("the.key", "tag")  // tag = p, div or li etc
// i18n("the.key", "tag", {a: <value>, b: <value>})  // tag = p, div or li etc
// i18n("the.key", { count: 5 })  // plural handling
// i18n("the.key", { returnObjects: true })  // return value as is without resolutions or expansions
export const i18n = (key, arg2, arg3) => {
    let tag = null;
    let params = {};

    if (typeof arg2 === 'string') {
        tag = arg2;
        params = arg3 || {};
    } else {
        params = arg2 || {};
    }

    // support the i18next notation with i18n("some.key", { returnObjects: true })
    const { returnObjects } = params;

    // --- plural handling ---
    if (params.count > 1) {
        const pluralKey = `${key}_plural`;
        const pluralValue = getValue(translations, pluralKey);

        if (pluralValue) {
            key = pluralKey;
        }
    }

    let value = getValue(translations, key);

    if (value == null) {
        console.warn(`Missing translation: ${key}`);
        return key;
    }

    if (returnObjects) {
        return value;
    }

    // --- object with variants (still supported if you use it) ---
    if (tag && typeof value === 'object' && !Array.isArray(value)) {
        value = value[tag];
    }

    // --- format result ---
    if (Array.isArray(value)) {
        const formatted = value.map(v => formatString(v, params));

        return formatted.map((item, index) => {
            const children = Array.isArray(item) ? item : [item];

            return React.createElement(tag, { key: index }, ...children);
        });
    }

    if (typeof value === 'string') {
        return formatString(value, params);
    }

    return value;
};
